import { usePublishedData } from "../lib/published-data";
import { createFileRoute, notFound, redirect } from "@tanstack/react-router";
import bibleStyles from "../styles/bible.css?url";
import { BibleContent } from "../components/bible/bible-content";
import { bibleHead } from "../components/bible/head";
import { DocumentContent } from "../components/document-content";
import { documentHead, useShell } from "../lib/document-route";
import { creatorPageHead, shellFrom, withStylesheet } from "../lib/head";
import { loadBible } from "../server/bible";

/**
 * League Bible (/bible): the page the creator composed, with the chapters at its built-in block.
 * Without one, the Bible opens its first chapter. With the Bible off, /bible is an ordinary page
 * of the creator's own.
 */
export const Route = createFileRoute("/bible/")({
  loader: async () => {
    const data = await loadBible();
    if (data.kind === "missing") throw notFound();
    if (data.kind === "redirect") throw redirect({ href: data.to, statusCode: 302 });
    return data;
  },
  head: ({ matches, loaderData }) => {
    const shell = shellFrom(matches);
    if (!shell || !loaderData) return {};
    if (loaderData.kind === "page" && !loaderData.contents)
      return documentHead(matches, loaderData, "/bible", {
        title: (shell) => `Page not found | ${shell.creator.name}`,
        description: () => "This page could not be found.",
      });
    const head =
      loaderData.kind === "page"
        ? creatorPageHead(shell, {
            pathname: "/bible",
            title: loaderData.page.seoTitle || loaderData.page.title,
            description: loaderData.page.description,
            canonicalPath: loaderData.page.path,
            noindex: loaderData.private || loaderData.locked || loaderData.page.hideFromSearch,
            preloadImage: loaderData.lead,
          })
        : creatorPageHead(shell, {
            pathname: "/bible",
            title: `${loaderData.contents.navTitle} | ${shell.creator.name}`,
            description: loaderData.contents.intro || `The League Bible by ${shell.creator.name}.`,
            canonicalPath: "/bible",
          });
    return bibleHead(withStylesheet(head, bibleStyles), shell);
  },
  component: BibleIndex,
});

function BibleIndex() {
  const initial = Route.useLoaderData();
  const shell = useShell();
  const { data } = usePublishedData(initial, shell, "/bible", "page");
  const contents = data.contents && <BibleContent contents={data.contents} />;
  return data.kind === "page" ? (
    <DocumentContent data={data} shell={shell} builtIn={contents} />
  ) : (
    contents
  );
}
